'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';
import { sendPaymentConfirmationEmail } from '@/lib/emailService';

interface OrderDetails {
  trackingNumber: string;
  serviceType: string;
  weight: string;
  dimensions: string;
  origin: string;
  destination: string;
  estimatedDelivery: string;
}

interface PaymentFormProps {
  orderDetails: OrderDetails;
  totalAmount: number;
  onPaymentComplete?: (paymentData: any) => void;
}

export default function PaymentForm({ orderDetails, totalAmount, onPaymentComplete }: PaymentFormProps) {
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal' | 'bank'>('card');
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    email: '',
    billingAddress: '',
  });
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);

    // Simulate payment processing
    setTimeout(async () => {
      setProcessing(false);
      setPaymentSuccess(true);
      analytics.trackConversion('payment_completed');
      analytics.trackUserAction('payment_submitted', {
        trackingNumber: orderDetails.trackingNumber,
        amount: totalAmount,
        method: paymentMethod,
      });

      // Send payment confirmation email
      if (formData.email) {
        await sendPaymentConfirmationEmail(
          formData.email,
          orderDetails.trackingNumber,
          totalAmount,
          paymentMethod
        );
      }

      if (onPaymentComplete) {
        onPaymentComplete({
          trackingNumber: orderDetails.trackingNumber,
          amount: totalAmount,
          method: paymentMethod,
          timestamp: new Date().toISOString(),
        });
      }
    }, 2000);
  };

  if (paymentSuccess) {
    return (
      <div className="bg-card rounded-xl p-8 shadow-card border border-border text-center">
        <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} className="text-success" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Payment Successful!</h3>
        <p className="text-muted-foreground mb-6">
          Your payment of ${totalAmount.toFixed(2)} has been processed successfully.
        </p>
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <p className="text-sm text-muted-foreground mb-1">Tracking Number</p>
          <p className="text-lg font-bold text-primary font-mono">{orderDetails.trackingNumber}</p>
        </div>
        <p className="text-sm text-muted-foreground">
          A confirmation email has been sent to your registered email address.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-6 shadow-card border border-border">
      <h3 className="text-xl font-heading font-semibold text-foreground mb-6 flex items-center gap-2">
        <Icon name="CreditCardIcon" size={24} className="text-primary" />
        Payment Details
      </h3>

      {/* Order Summary */}
      <div className="bg-muted rounded-lg p-4 mb-6">
        <h4 className="text-sm font-semibold text-foreground mb-3">Order Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Tracking Number:</span>
            <span className="font-mono font-medium text-foreground">{orderDetails.trackingNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Service Type:</span>
            <span className="font-medium text-foreground">{orderDetails.serviceType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Route:</span>
            <span className="font-medium text-foreground">
              {orderDetails.origin} → {orderDetails.destination}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Weight:</span>
            <span className="font-medium text-foreground">{orderDetails.weight}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Dimensions:</span>
            <span className="font-medium text-foreground">{orderDetails.dimensions}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Est. Delivery:</span>
            <span className="font-medium text-foreground">{orderDetails.estimatedDelivery}</span>
          </div>
          <div className="border-t border-border pt-2 mt-2 flex justify-between">
            <span className="font-semibold text-foreground">Total Amount:</span>
            <span className="font-bold text-primary text-lg">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-3">Payment Method</label>
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setPaymentMethod('card')}
            className={`p-4 border-2 rounded-lg transition-all flex flex-col items-center gap-2 ${
              paymentMethod === 'card' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <Icon name="CreditCardIcon" size={24} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Card</span>
          </button>
          <button
            type="button"
            onClick={() => setPaymentMethod('paypal')}
            className={`p-4 border-2 rounded-lg transition-all flex flex-col items-center gap-2 ${
              paymentMethod === 'paypal' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <Icon name="CurrencyDollarIcon" size={24} className="text-primary" />
            <span className="text-sm font-medium text-foreground">PayPal</span>
          </button>
          <button
            type="button"
            onClick={() => setPaymentMethod('bank')}
            className={`p-4 border-2 rounded-lg transition-all flex flex-col items-center gap-2 ${
              paymentMethod === 'bank' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <Icon name="BuildingLibraryIcon" size={24} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Bank</span>
          </button>
        </div>
      </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {paymentMethod === 'card' && (
          <>
            <div>
              <label htmlFor="cardNumber" className="block text-sm font-medium text-foreground mb-2">
                Card Number *
              </label>
              <input
                id="cardNumber"
                name="cardNumber"
                type="text"
                required
                value={formData.cardNumber}
                onChange={handleChange}
                placeholder="1234 5678 9012 3456"
                maxLength={19}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
            <div>
              <label htmlFor="cardName" className="block text-sm font-medium text-foreground mb-2">
                Cardholder Name *
              </label>
              <input
                id="cardName"
                name="cardName"
                type="text"
                required
                value={formData.cardName}
                onChange={handleChange}
                placeholder="John Doe"
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiryDate" className="block text-sm font-medium text-foreground mb-2">
                  Expiry Date *
                </label>
                <input
                  id="expiryDate"
                  name="expiryDate"
                  type="text"
                  required
                  value={formData.expiryDate}
                  onChange={handleChange}
                  placeholder="MM/YY"
                  maxLength={5}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                />
              </div>
              <div>
                <label htmlFor="cvv" className="block text-sm font-medium text-foreground mb-2">
                  CVV *
                </label>
                <input
                  id="cvv"
                  name="cvv"
                  type="text"
                  required
                  value={formData.cvv}
                  onChange={handleChange}
                  placeholder="123"
                  maxLength={4}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                />
              </div>
            </div>
          </>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Email Address *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
          />
        </div>

        <div>
          <label htmlFor="billingAddress" className="block text-sm font-medium text-foreground mb-2">
            Billing Address *
          </label>
          <input
            id="billingAddress"
            name="billingAddress"
            type="text"
            required
            value={formData.billingAddress}
            onChange={handleChange}
            placeholder="123 Main St, City, Country"
            className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
          />
        </div>

        <button
          type="submit"
          disabled={processing}
          className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Processing Payment...</span>
            </>
          ) : (
            <>
              <Icon name="LockClosedIcon" size={20} />
              <span>Pay ${totalAmount.toFixed(2)}</span>
            </>
          )}
        </button>

        <p className="text-xs text-muted-foreground text-center">
          <Icon name="ShieldCheckIcon" size={14} className="inline mr-1" />
          Your payment information is secure and encrypted
        </p>
      </form>
    </div>
  );
}
